import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { UserCog, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchUserAiProfiles } from "@/api/userAiProfiles";

export default function UserAiProfilesList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ["user-ai-profiles"],
    queryFn: fetchUserAiProfiles,
  });

  const filtered = profiles.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.userName?.toLowerCase().includes(q) ||
      p.userEmail?.toLowerCase().includes(q) ||
      p.detectedProfession?.toLowerCase().includes(q) ||
      p.topicsOfInterest.some((t) => t.toLowerCase().includes(q))
    );
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout title="User AI Profiles">
      <div className="space-y-6">
        <p className="text-muted-foreground">
          AI-detected user profiles built from chat behavior. Read-only — profiles are updated automatically.
        </p>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Profiles ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, profession, or topic..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Profession</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Reading Level</TableHead>
                  <TableHead>Topics</TableHead>
                  <TableHead className="text-center">Signals</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No profiles found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((profile) => (
                    <TableRow
                      key={profile.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/admin/ai/user-profiles/${profile.id}`)}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">{profile.userName || "Unknown"}</div>
                          <div className="text-xs text-muted-foreground">{profile.userEmail || ""}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {profile.detectedProfession ? (
                          <div>
                            <span>{profile.detectedProfession}</span>
                            <span className="ml-1 text-xs text-muted-foreground">
                              ({Math.round(profile.professionConfidence * 100)}%)
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {profile.languagePreference ? (
                          <Badge variant="outline">{profile.languagePreference}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {profile.readingLevel ? (
                          <Badge variant="secondary">{profile.readingLevel}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        {profile.topicsOfInterest.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {profile.topicsOfInterest.slice(0, 3).map((t) => (
                              <Badge key={t} variant="outline" className="text-xs">
                                {t}
                              </Badge>
                            ))}
                            {profile.topicsOfInterest.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{profile.topicsOfInterest.length - 3}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{profile.signalCount}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(profile.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
